import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Tooltip from '@atlaskit/tooltip';
import { Text } from '@atlaskit/primitives';
import { CopyableText } from '@css-xp/base';
import { getInvoiceGroupName } from '@css-xp/billing-v2';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { routePaths } from '@css-xp/ppc-constants';
import { fetchBillingProfileNameById } from './queries/fetch-billing-profile-name-by-id';
import { NoData } from './no-data';

export const BillingProfileNameFromId: React.FC<{
  id?: string | null;
  txaId: string;
  beforeLinkText?: string;
  skeletonLoaderWidth?: string;
  showBillingProfileNumber?: boolean;
  invoiceGroupData?: generatedTypesNew.FetchBillingProfileNameByIdQuery | null;
}> = ({
  id,
  txaId,
  beforeLinkText,
  skeletonLoaderWidth = '100%',
  showBillingProfileNumber = false,
  invoiceGroupData,
}) => {
  const { data: billingProfileData, loading, error } = useQuery<
    generatedTypesNew.FetchBillingProfileNameByIdQuery,
    generatedTypesNew.FetchBillingProfileNameByIdQueryVariables
  >(fetchBillingProfileNameById, {
    variables: {
      id: id ?? '',
      txaId,
    },
    displayName: 'fetchBillingProfileNameById',
    fetchPolicy: 'cache-and-network',
    skip: !id || !!invoiceGroupData?.invoiceGroup,
  });
  const data = invoiceGroupData ?? billingProfileData;
  if (error) {
    return <NoData />;
  }

  if (loading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader marginBottom="0" width={skeletonLoaderWidth} />
      </SkeletonLoaderWrapper>
    );
  }

  const billingProfileName = getInvoiceGroupName(data?.invoiceGroup?.name);

  return id ? (
    <>
      <Tooltip content={billingProfileName} tag="span">
        <Link
          to={`${getRouteUrl(routePaths.billingProfileDetails, {
            txaId: txaId,
            billingProfileId: `${encodeURIComponent(id)}`,
          })}`}
          onClick={(e) => e.stopPropagation()}
        >
          {beforeLinkText} {billingProfileName}
        </Link>
      </Tooltip>
      {showBillingProfileNumber && (
        <p className="billing-profile-id-container">
          <Text size="small" color="color.text.subtlest">
            <CopyableText text={data?.invoiceGroup?.invoiceGroupNumber} duration={0} />
          </Text>
        </p>
      )}
    </>
  ) : (
    <NoData />
  );
};
